import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Check, RefreshCw, Dumbbell, AlertTriangle, Zap, BarChart2, Battery } from 'lucide-react';
// Local types for the parsed planner workout
export interface VoiceParsedSetSpec {
  sets: number;
  repsMin: number;
  repsMax: number;
  rpe?: number;
  rir?: number;
}
export interface VoiceParsedExercise {
  name: string;
  unrecognized?: boolean;
  trackingMode?: 'overload' | 'hypertrophy' | 'deload';
  sets: VoiceParsedSetSpec[];
}
export interface VoiceParsedWorkout {
  workoutName?: string;
  exercises: VoiceParsedExercise[];
}

interface VoiceWorkoutPreviewCardProps {
  parsed: VoiceParsedWorkout;
  onAccept: (workout: VoiceParsedWorkout) => void;
  onReRecord: () => void;
  onDismiss: () => void;
}

const modeIcon = (mode?: string) => {
  if (mode === 'hypertrophy') return <BarChart2 className="h-2.5 w-2.5 text-emerald-400" />;
  if (mode === 'deload') return <Battery className="h-2.5 w-2.5 text-blue-400" />;
  return <Zap className="h-2.5 w-2.5 text-yellow-400" />;
};

const modeColor = (mode?: string) => {
  if (mode === 'hypertrophy') return 'bg-emerald-400/10 text-emerald-400 border-emerald-400/30';
  if (mode === 'deload') return 'bg-blue-400/10 text-blue-400 border-blue-400/30';
  return 'bg-yellow-400/10 text-yellow-400 border-yellow-400/30';
};

export function VoiceWorkoutPreviewCard({ parsed, onAccept, onReRecord, onDismiss }: VoiceWorkoutPreviewCardProps) {
  const [workoutName, setWorkoutName] = useState(parsed.workoutName || '');
  const [exercises, setExercises] = useState<VoiceParsedExercise[]>(parsed.exercises);

  const handleUpdateExercise = (idx: number, field: keyof VoiceParsedExercise, value: any) => {
    setExercises(prev => prev.map((ex, i) => i === idx ? { ...ex, [field]: value } : ex));
  };

  const handleRemoveExercise = (idx: number) => {
    setExercises(prev => prev.filter((_, i) => i !== idx));
  };

  const handleAccept = () => {
    onAccept({ ...parsed, workoutName, exercises });
  };

  const unrecognizedCount = exercises.filter(ex => ex.unrecognized).length;

  return (
    <div className="forge-panel p-4 space-y-3 border-primary/30 bg-primary/5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono font-bold text-primary uppercase tracking-wider">🎙 Voice Preview</span>
          {unrecognizedCount > 0 && (
            <Badge variant="outline" className="text-[9px] font-mono bg-amber-500/10 text-amber-400 border-amber-500/30 gap-1">
              <AlertTriangle className="h-2.5 w-2.5" />
              {unrecognizedCount} unrecognized
            </Badge>
          )}
        </div>
        <button
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground text-xs font-mono"
        >
          ✕
        </button>
      </div>

      {/* Workout Name */}
      <div>
        <label className="block text-[9px] font-mono text-muted-foreground uppercase mb-1">Workout Name</label>
        <Input
          value={workoutName}
          onChange={(e) => setWorkoutName(e.target.value)}
          className="bg-secondary border-border text-sm font-semibold text-primary h-8"
          placeholder="e.g. Upper Push"
        />
      </div>

      {/* Exercise List */}
      <div className="space-y-2">
        {exercises.map((ex, idx) => (
          <div
            key={idx}
            className={`rounded-sm border p-2.5 space-y-1.5 ${
              ex.unrecognized
                ? 'border-amber-500/40 bg-amber-500/5'
                : 'border-border bg-secondary/50'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <Dumbbell className={`h-3 w-3 shrink-0 ${ex.unrecognized ? 'text-amber-400' : 'text-primary'}`} />
                <span className={`text-xs font-mono font-bold truncate ${ex.unrecognized ? 'text-amber-400' : 'text-foreground'}`}>
                  {ex.name}
                </span>
                {ex.unrecognized && (
                  <span className="text-[8px] font-mono text-amber-400/70 shrink-0">NEW</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {/* Mode badge */}
                <span className={`inline-flex items-center gap-0.5 text-[8px] font-mono font-bold px-1 py-0.5 rounded-sm border ${modeColor(ex.trackingMode)}`}>
                  {modeIcon(ex.trackingMode)}
                  {(ex.trackingMode ?? 'overload').toUpperCase().slice(0, 3)}
                </span>
                <button
                  onClick={() => handleRemoveExercise(idx)}
                  className="text-muted-foreground hover:text-destructive text-[10px] font-mono"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Sets summary */}
            <div className="flex flex-wrap gap-1">
              {ex.sets.map((set: VoiceParsedSetSpec, si: number) => (
                <span key={si} className="text-[9px] font-mono bg-background border border-border px-1.5 py-0.5 rounded-sm text-muted-foreground">
                  {set.sets}×{set.repsMin === set.repsMax ? set.repsMin : `${set.repsMin}–${set.repsMax}`}
                  {set.rpe !== undefined ? ` @RPE${set.rpe}` : ''}
                  {set.rir !== undefined ? ` RIR${set.rir}` : ''}
                </span>
              ))}
            </div>

            {/* Inline name correction for unrecognized */}
            {ex.unrecognized && (
              <div>
                <label className="block text-[8px] font-mono text-amber-400/70 uppercase mb-0.5">Correct name (or leave to create new)</label>
                <Input
                  value={ex.name}
                  onChange={(e) => handleUpdateExercise(idx, 'name', e.target.value)}
                  className="bg-background border-amber-500/30 text-xs h-7 font-mono"
                  placeholder="Exercise name..."
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {exercises.length === 0 && (
        <div className="text-center py-3 text-xs font-mono text-muted-foreground">
          No exercises parsed — try re-recording
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 pt-1">
        <Button
          onClick={handleAccept}
          disabled={exercises.length === 0}
          size="sm"
          className="flex-1 font-mono text-xs gap-1.5"
        >
          <Check className="h-3.5 w-3.5" />
          SAVE WORKOUT
        </Button>
        <Button
          onClick={onReRecord}
          variant="outline"
          size="sm"
          className="font-mono text-xs gap-1.5"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          RE-RECORD
        </Button>
      </div>
    </div>
  );
}
